import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { 
  FaUser, 
  FaLock, 
  FaBell, 
  FaShieldAlt, 
  FaCog,
  FaArrowRight
} from "react-icons/fa";

const Settings = () => {
  const { user } = useAuth();

  const settingsOptions = [
    {
      id: "profile",
      title: "Edit Profile",
      description: "Update your personal information, profile picture, and bio",
      icon: <FaUser className="text-[#1E40AF]" size={24} />,
      to: "/profile/edit",
      color: "blue"
    },
    {
      id: "password",
      title: "Change Password",
      description: "Update your password for better security",
      icon: <FaLock className="text-[#F59E0B]" size={24} />,
      to: "/settings/password",
      color: "orange"
    },
    {
      id: "notifications",
      title: "Notifications",
      description: "Manage your notification preferences",
      icon: <FaBell className="text-[#1E40AF]" size={24} />,
      to: "/settings/notifications",
      color: "blue"
    },
    {
      id: "privacy",
      title: "Privacy & Security",
      description: "Control your privacy settings and account security",
      icon: <FaShieldAlt className="text-[#F59E0B]" size={24} />,
      to: "/settings/privacy",
      color: "orange"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="mb-8">
            <h1 
              className="text-4xl font-bold mb-2"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 700,
                color: '#1F2937'
              }}
            >
              Settings
            </h1>
            <p 
              className="text-gray-600"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif"
              }}
            >
              Manage your account settings and preferences
            </p>
          </div>

          {/* User Info Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <FaUser className="text-[#1E40AF]" size={32} />
              </div>
              <div>
                <h2 
                  className="text-xl font-semibold"
                  style={{
                    fontFamily: "'Open Sans', system-ui, sans-serif",
                    fontWeight: 600,
                    color: '#1F2937'
                  }}
                >
                  {user?.firstName} {user?.surname}
                </h2>
                <p 
                  className="text-gray-600"
                  style={{
                    fontFamily: "'Open Sans', system-ui, sans-serif"
                  }}
                >
                  {user?.email}
                </p>
                <span 
                  className="inline-block mt-1 px-3 py-1 rounded-full text-xs font-semibold capitalize"
                  style={{
                    fontFamily: "'Open Sans', system-ui, sans-serif",
                    backgroundColor: user?.role === 'admin' ? '#FEE2E2' : '#DBEAFE',
                    color: user?.role === 'admin' ? '#DC2626' : '#1E40AF'
                  }}
                >
                  {user?.role || 'user'}
                </span>
              </div>
            </div>
          </div>

          {/* Settings Options Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {settingsOptions.map((option) => (
              <Link
                key={option.id}
                to={option.to}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-300 group flex items-start justify-between"
                style={{
                  fontFamily: "'Open Sans', system-ui, sans-serif"
                }}
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                    {option.icon}
                  </div>
                  <div className="flex-1">
                    <h3 
                      className="text-lg font-semibold mb-1 group-hover:text-[#F59E0B] transition-colors"
                      style={{
                        fontFamily: "'Open Sans', system-ui, sans-serif",
                        fontWeight: 600,
                        color: '#1F2937'
                      }}
                    >
                      {option.title}
                    </h3>
                    <p 
                      className="text-sm text-gray-600"
                      style={{
                        fontFamily: "'Open Sans', system-ui, sans-serif"
                      }}
                    >
                      {option.description}
                    </p>
                  </div>
                </div>
                <FaArrowRight 
                  className="text-gray-400 group-hover:text-[#F59E0B] group-hover:translate-x-1 transition-all flex-shrink-0 ml-4" 
                  size={20}
                />
              </Link>
            ))}
          </div>

          {/* Admin Settings Link */}
          {user?.role === 'admin' && (
            <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <Link
                to="/admin/settings"
                className="flex items-center justify-between group"
                style={{
                  fontFamily: "'Open Sans', system-ui, sans-serif"
                }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <FaCog className="text-[#F59E0B]" size={24} />
                  </div>
                  <div>
                    <h3 
                      className="text-lg font-semibold mb-1 group-hover:text-[#F59E0B] transition-colors"
                      style={{
                        fontFamily: "'Open Sans', system-ui, sans-serif",
                        fontWeight: 600,
                        color: '#1F2937'
                      }}
                    >
                      System Settings
                    </h3>
                    <p 
                      className="text-sm text-gray-600"
                      style={{
                        fontFamily: "'Open Sans', system-ui, sans-serif"
                      }}
                    >
                      Manage system-wide settings and configurations (Admin only)
                    </p>
                  </div>
                </div>
                <FaArrowRight 
                  className="text-gray-400 group-hover:text-[#F59E0B] group-hover:translate-x-1 transition-all" 
                  size={20}
                />
              </Link>
            </div>
          )}
        </div>
      </div>
  );
};

export default Settings;

